import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../common/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../common/Dialog";
import { Input } from "../common/Input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../common/Select";
import { NumberInput } from "../common/NumberInput";
import { useTheme } from "../common/ThemeContext";
import { fetchAuth } from "../common/api";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const SurveyEditor = ({ survey, onClose, onSave }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [draft, setDraft] = useState(survey || { name: "", questions: [] });
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [tags, setTags] = useState([]);
  const KEY_MAX   = 50;
  const LABEL_MAX = 50;

  const findDuplicateKeys = qs => {
    const map = {};
    qs.forEach((q, idx) => {
      map[q.key] = map[q.key] ? [...map[q.key], idx] : [idx];
    });
    return Object.fromEntries(
      Object.entries(map).filter(([, arr]) => arr.length > 1)
    );
  };

  const slugify = str =>
    str.trim().toLowerCase()
       .replace(/\s+/g, "_")     // espaces → _
       .replace(/[^\w_]/g, "")  // ponctuation retirée
       .slice(0, KEY_MAX);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await fetchAuth("/question_tags");
        if (!response.ok) throw new Error("Failed to fetch tags");
        const data = await response.json();
        setTags(data);
      } catch (error) {
       
      }
    };

    fetchTags();
  }, []);

  useEffect(() => {
    const fetchMandatoryQuestions = async () => {
      try {
        const res = await fetchAuth("/question_library/mandatory");
        if (!res.ok) throw new Error("Failed to fetch mandatory questions");
        const mandatory = await res.json();        // [{id,key,label…}, …]
    
        setDraft(prev => {
          // jeux de repères pour savoir si la question existe déjà
          const mandIds  = new Set(mandatory.map(m => m.id));
          const mandKeys = new Set(mandatory.map(m => m.key));
    
          // a) on *marque* comme mandatory toutes les questions déjà là
          const already = prev.questions.map(q =>
            (mandIds.has(q.library_id) || mandKeys.has(q.key))
              ? { ...q, is_mandatory: true }
              : q
          );
    
          // b) on *ajoute* celles qui manquent encore
          const existingLibIds = new Set(
              prev.questions            // on récupère à la fois
                .map(q => q.library_id) // • les library_id (si déjà stockés)
                .filter(Boolean)        //   (filtre undefined / null)
            );
            const existingKeys = new Set( // • les keys (toujours présentes)
              prev.questions.map(q => q.key)
            );
          const toAdd = mandatory
            .filter(m => !existingLibIds.has(m.id) && !existingKeys.has(m.key))
            .map(m => ({
              ...m,
              library_id : m.id,
              is_mandatory: true,
              tag        : m.tag,          // conserve le tag nommé
              options    : m.options ?? [],
              config     : m.config  ?? {},
              type       : m.type,
              id         : m.id,           // identifiant unique (library)
            }));
    
          return { ...prev, questions: [...toAdd, ...already] };
        });
      } catch (err) {
        
      }
    };

    fetchMandatoryQuestions();

  }, [survey]);

  const handleAddQuestion = () => {
    const defaultTag = tags.length > 0 ? tags[0].code : "unknown"; // Use the first tag in the list or "unknown" as fallback
    setEditingQuestion({
      id: Date.now(),
      label: t("survey_editor.new_question"),
      type: "scale",
      tag: defaultTag,
      config: { min: 1, max: 5, step: 1, gradient: { low: "#DC2626", high: "#22C55E" } },
      options: [],
    });
  };

  

  const handleSaveQuestion = (question) => {
    const updatedQuestions = draft.questions.map((q) =>
      q.id === question.id ? question : q
    );
    if (!draft.questions.some((q) => q.id === question.id)) {
      updatedQuestions.push(question);
    }

    // Assure que le tag sélectionné est correctement assigné ou utilise le premier tag par défaut
    const selectedTag = tags.find((tag) => tag.id === question.tag_id) || tags[0];
    const updatedQuestion = {
      ...question,
      tag: selectedTag ? selectedTag.code : "unknown", // Utilise le code du tag sélectionné ou le premier tag par défaut
    };

    setDraft({ ...draft, questions: updatedQuestions.map((q) => (q.id === question.id ? updatedQuestion : q)) });
    setEditingQuestion(null);
  };

  const handleDeleteQuestion = async (id) => {
    const question = draft.questions.find((q) => q.id === id);
    if (question?.is_mandatory) {
      alert(t("survey_editor.errors.mandatory_cannot_delete"));
      return;
    }

    // guard: at least 1 active question must remain
    const activeCount = draft.questions.filter(q => q.active !== false && q.id !== id).length;
    if (activeCount < 1) {
      alert(t("survey_editor.errors.cannot_delete_last_active"));
      return;
    }

    try {
      const response = await fetchAuth(`/surveys/question/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert(err.message || t("survey_editor.errors.delete_failed"));
        return;
      }
      setDraft({ ...draft, questions: draft.questions.filter((q) => q.id !== id) });
    } catch (error) {
      
    }
  };

  const handleToggleMandatory = async (id) => {
    const question = draft.questions.find((q) => q.id === id);
    if (!question) return;

    // guard: cannot deactivate last active question
    if (question.active !== false) {
      const activeCount = draft.questions.filter(q => q.active !== false && q.id !== id).length;
      if (activeCount < 1) {
        alert(t("survey_editor.errors.cannot_deactivate_last_active"));
        return;
      }
    }

    // if survey is already saved (has a real UUID id), call the toggle API
    if (draft.id) {
      try {
        const res = await fetchAuth(`/surveys/question/${id}/toggle`, { method: "PATCH" });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          alert(err.message || t("survey_editor.errors.toggle_failed"));
          return;
        }
        const body = await res.json();
        setDraft(prev => ({
          ...prev,
          questions: prev.questions.map(q => q.id === id ? { ...q, active: body.active } : q),
        }));
      } catch (error) {
        alert(t("survey_editor.errors.network_error"));
      }
    } else {
      // survey not yet saved — just toggle locally
      setDraft(prev => ({
        ...prev,
        questions: prev.questions.map(q =>
          q.id === id ? { ...q, active: q.active === false ? true : false } : q
        ),
      }));
    }
  };

  const handleSaveSurvey = async () => {
    /* ─── construction + log immédiat ───────────────────────────── */
    const isEditingExisting = Boolean(draft.id);
    const basePayload = {
      name      : (draft.name || "").trim(),
      code      : draft.code || "custom",
      is_active : draft.is_active ?? true,
      questions : draft.questions.map((q, i) => ({
        id      : q.id,
        label   : q.label,
        type    : q.type,
        active  : q.active !== false,
        tag_name:
          typeof q.tag === "string"
            ? q.tag
            : q.tag?.code ?? "unknown",
        config  : q.config,
        options : q.options,
        order   : i,
        key     : q.key || slugify(q.label),
      })),
    };
    const payload = {
      ...basePayload,
      ...(isEditingExisting && draft.version ? { version: draft.version } : {}),
    };
  
    
  
    /* ─── validations côté front ───────────────────────────────── */
    if (!payload.name) {
      return alert(t("survey_editor.errors.title_required"));
    }
    if (payload.questions.length === 0 || payload.questions.filter(q => q.active).length === 0) {
      return alert(t("survey_editor.errors.active_question_required"));
    }
  
    // détection des doublons de key
    const dups = findDuplicateKeys(payload.questions);
    if (Object.keys(dups).length) {
      const details = Object.entries(dups)
        .map(([k, idcs]) =>
          t("survey_editor.errors.duplicate_key_line", {
            key: k,
            questions: idcs
              .map(i =>
                t("survey_editor.errors.duplicate_key_question", {
                  index: i + 1,
                  label: payload.questions[i].label,
                })
              )
              .join(", "),
          })
        )
        .join("\n");
      return alert(t("survey_editor.errors.duplicate_keys_header", { details }));
    }
  
    /* ─── appel API ─────────────────────────────────────────────── */
    try {
      const res = await fetchAuth(
        isEditingExisting ? `/surveys/${draft.id}` : "/surveys",
        {
          method : isEditingExisting ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body   : JSON.stringify(payload),
        }
      );
  
      if (!res.ok) {
        let reason = t("survey_editor.errors.unknown_error");
        try {
          const err = await res.json();
          reason = err.message || err.error || JSON.stringify(err);
        } catch {/* ignore */}
        return alert(t("survey_editor.errors.save_refused", { reason }));
      }
  
      const saved = await res.json();
      onSave(saved);
      setEditingQuestion(null);
    } catch (e) {
     
      alert(t("survey_editor.errors.network_or_server"));
    }
  };

  const handleLabelChange = (e) => {
    // Truncate immediately
    const raw    = e.target.value;
    const label  = raw.slice(0, LABEL_MAX);   // ← coupe au max autorisé
  
    /* --- recalcule une key unique ———————————————— */
    const existing = new Set(
      draft.questions
           .filter(q => q.id !== editingQuestion.id)
           .map(q => q.key)
    );
    const newKeyBase = slugify(label);
    let key = newKeyBase, n = 1;
    while (existing.has(key)) {
      key = `${newKeyBase.slice(0, KEY_MAX - (`_${n}`.length))}_${n++}`;
    }
  
    setEditingQuestion({ ...editingQuestion, label, key });
  };

  const withDefaults = (q) => {
    if (q.type !== "scale") return q;
    const g = q.config?.gradient;
    return {
      ...q,
      config: {
        min: q.config?.min ?? 1,
        max: q.config?.max ?? 5,
        step: q.config?.step ?? 1,
        ...q.config,
        gradient: {
          low:  g?.low  ?? "#DC2626",
          high: g?.high ?? "#22C55E",
        },
      },
    };
  };

  const isMandatoryScaleEditor =
    editingQuestion?.is_mandatory && editingQuestion?.type === "scale";

  const invertScaleGradient = () => {
    if (!editingQuestion) return;
    const low = editingQuestion.config?.gradient?.low ?? "#DC2626";
    const high = editingQuestion.config?.gradient?.high ?? "#22C55E";
    setEditingQuestion({
      ...editingQuestion,
      config: {
        ...editingQuestion.config,
        gradient: { low: high, high: low },
      },
    });
  };

  const renderQuestionEditor = () => {
    if (!editingQuestion) return null;

    return (
      <Dialog open onOpenChange={(isOpen) => isOpen || setEditingQuestion(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center text-2xl font-semibold text-indigo-600">
              {isMandatoryScaleEditor
                ? t("survey_editor.configure_scale_direction")
                : t("survey_editor.edit_question")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
          {isMandatoryScaleEditor ? (
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("survey_editor.question")}</p>
              <p className="text-base text-gray-900 dark:text-gray-100">{editingQuestion.label}</p>
              <p className="text-xs text-amber-500 mt-1">
                {t("survey_editor.mandatory_scale_hint")}
              </p>
            </div>
          ) : (
          <>
          <Input
            label={t("survey_editor.question_label")}
            value={editingQuestion.label}
            onChange={handleLabelChange}
            className={`w-full px-4 py-2 border rounded-lg shadow-sm 
                    bg-white text-gray-900 
                    dark:bg-gray-800 dark:text-gray-100 
                    focus:ring focus:ring-blue-300`}
          />
            <span className="text-xs text-gray-500">
              {editingQuestion.label.length}/{LABEL_MAX}
            </span>
            <Select
            className="w-full px-4 py-2 border rounded-lg shadow-sm focus:ring focus:ring-blue-300 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
              label={t("survey_editor.question_type")}
              value={editingQuestion.type}
              onValueChange={(type) => setEditingQuestion({ ...editingQuestion, type })}
            >
              <SelectTrigger className="border-indigo-300 focus:ring-indigo-500">
                <SelectValue placeholder={t("survey_editor.select_type")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scale">{t("survey_editor.types.scale")}</SelectItem>
                <SelectItem value="choice">{t("survey_editor.types.choice")}</SelectItem>
                <SelectItem value="text">{t("survey_editor.types.text")}</SelectItem>
                <SelectItem value="yesno">{t("survey_editor.types.yesno")}</SelectItem>
                <SelectItem value="time">{t("survey_editor.types.time")}</SelectItem>
                <SelectItem value="bodymap">{t("survey_editor.types.bodymap")}</SelectItem>
              </SelectContent>
            </Select>
            {editingQuestion.type !== "bodymap" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("survey_editor.tag")}
                </label>
                <Select
                  value={editingQuestion.tag_id || ""}
                  onValueChange={(tagId) => setEditingQuestion({ ...editingQuestion, tag_id: tagId })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("survey_editor.select_tag")} />
                  </SelectTrigger>
                  <SelectContent>
                    {tags.map((tag) => (
                      <SelectItem key={tag.id} value={tag.id}>
                        {tag.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
          )}
            {editingQuestion.type === "scale" && (
              <div className="space-y-4">
                {!isMandatoryScaleEditor && (
              <div className="grid grid-cols-3 gap-4">
                <NumberInput
                  label={t("survey_editor.min")}
                  value={editingQuestion.config.min}
                  onChange={(value) => {
                    if (value < 0 || value > 9) {
                      alert(t("survey_editor.errors.min_range"));
                      return;
                    }
                    setEditingQuestion({
                      ...editingQuestion,
                      config: { ...editingQuestion.config, min: value },
                    });
                  }}
                  className="border-indigo-300 focus:ring-indigo-500"
                />
                <NumberInput
                  label={t("survey_editor.max")}
                  value={editingQuestion.config.max}
                  onChange={(value) => {
                    if (value < 1 || value > 10) {
                      alert(t("survey_editor.errors.max_range"));
                      return;
                    }
                    setEditingQuestion({
                      ...editingQuestion,
                      config: { ...editingQuestion.config, max: value },
                    });
                  }}
                  className="border-indigo-300 focus:ring-indigo-500"
                />
              </div>
                )}
                {/* Gradient editor */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("survey_editor.scale_direction")}
                    </p>
                    <Button
                      type="button"
                      onClick={invertScaleGradient}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 text-sm rounded-lg"
                    >
                      {t("survey_editor.invert_scale")}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {t("survey_editor.scale_direction_hint", {
                      min: editingQuestion.config?.min ?? 1,
                      max: editingQuestion.config?.max ?? 5,
                    })}
                  </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("survey_editor.color_at_minimum", {
                        value: editingQuestion.config?.min ?? 1,
                      })}
                    </label>
                    <input
                      type="color"
                      value={editingQuestion.config?.gradient?.low || "#DC2626"}
                      onChange={(e) =>
                        setEditingQuestion({
                          ...editingQuestion,
                          config: {
                            ...editingQuestion.config,
                            gradient: {
                              ...(editingQuestion.config?.gradient || {}),
                              low: e.target.value,
                            },
                          },
                        })
                      }
                      className="h-10 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("survey_editor.color_at_maximum", {
                        value: editingQuestion.config?.max ?? 5,
                      })}
                    </label>
                    <input
                      type="color"
                      value={editingQuestion.config?.gradient?.high || "#22C55E"}
                      onChange={(e) =>
                        setEditingQuestion({
                          ...editingQuestion,
                          config: {
                            ...editingQuestion.config,
                            gradient: {
                              ...(editingQuestion.config?.gradient || {}),
                              high: e.target.value,
                            },
                          },
                        })
                      }
                      className="h-10 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-transparent"
                    />
                  </div>
                </div>
                </div>

                {/* Preview rendu */}
                <div className="rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <p className="text-sm mb-2 text-gray-600 dark:text-gray-300">
                    {t("survey_editor.slider_preview")}
                  </p>

                  <div
                    className="h-3 rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${editingQuestion.config?.gradient?.low || "#DC2626"}, ${editingQuestion.config?.gradient?.high || "#22C55E"})`,
                    }}
                  />

                  {/* mini curseur visuel (optionnel) */}
                  <input
                    type="range"
                    min={editingQuestion.config?.min ?? 1}
                    max={editingQuestion.config?.max ?? 5}
                    step={editingQuestion.config?.step ?? 1}
                    defaultValue={Math.round(((editingQuestion.config?.min ?? 1) + (editingQuestion.config?.max ?? 5)) / 2)}
                    className="w-full mt-3"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{editingQuestion.config?.min ?? 1}</span>
                    <span>{editingQuestion.config?.max ?? 5}</span>
                  </div>
                </div>
              </div>
            )}
            {!isMandatoryScaleEditor && editingQuestion.type === "choice" && (
              <div className="space-y-2">
                {editingQuestion.options.map((opt, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <Input
                      value={opt.label}
                      onChange={(e) => {
                        const updatedOptions = [...editingQuestion.options];
                        updatedOptions[idx] = {
                          ...updatedOptions[idx],
                          label: e.target.value,
                        };
                        setEditingQuestion({
                          ...editingQuestion,
                          options: updatedOptions,
                        });
                      }}
                      placeholder={t("survey_editor.option_placeholder", { n: idx + 1 })}
                      className="border-indigo-300 focus:ring-indigo-500"
                    />
                    <Button
                      onClick={() => {
                        const updatedOptions = editingQuestion.options.filter(
                          (_, i) => i !== idx
                        );
                        setEditingQuestion({
                          ...editingQuestion,
                          options: updatedOptions,
                        });
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                    >
                      {t("survey_editor.remove")}
                    </Button>
                  </div>
                ))}
                <Button
                  onClick={() => {
                    if (editingQuestion.config.min >= editingQuestion.config.max) {
                      alert(t("survey_editor.errors.min_gte_max"));
                      return;
                    }
                    setEditingQuestion({
                      ...editingQuestion,
                      options: [
                        ...editingQuestion.options,
                        { label: "", value: crypto.randomUUID() },
                      ],
                    });
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                >
                  {t("survey_editor.add_option")}
                </Button>
              </div>
            )}
            {!isMandatoryScaleEditor && editingQuestion.type === "time" && (
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("survey_editor.time_format")}
                </label>
                <Select
                  value={editingQuestion.config?.mode || "date"}
                  onValueChange={(mode) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      config: { ...editingQuestion.config, mode },
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("survey_editor.select_format")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">{t("survey_editor.time_modes.date")}</SelectItem>
                    <SelectItem value="time">{t("survey_editor.time_modes.time")}</SelectItem>
                    <SelectItem value="datetime">{t("survey_editor.time_modes.datetime")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button onClick={() => setEditingQuestion(null)} className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg">
              {t("survey_editor.cancel")}
            </Button>
            <Button
              onClick={() => {
                if (editingQuestion.type === "scale") {
                  const { min, max } = editingQuestion.config;
                  if (min >= max) {
                    alert(t("survey_editor.errors.min_gte_max_adjust"));
                    return;
                  }
                }
                handleSaveQuestion(editingQuestion);
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg"
            >
              {t("survey_editor.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className={`fixed inset-0 flex justify-center items-center transition-colors duration-300 z-[60] ${isDarkMode ? "bg-black bg-opacity-50" : "bg-white bg-opacity-90"}`}>
      <div className={`p-8 rounded-lg shadow-lg w-3/4 max-h-[80vh] overflow-y-auto transition-colors duration-300 ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"}`}>
        <h2 className={`text-3xl font-bold mb-6 text-center ${isDarkMode ? "text-indigo-300" : "text-indigo-600"}`}>
          {survey ? t("survey_editor.edit_survey") : t("survey_editor.create_survey")}
        </h2>
        <Input
          label={t("survey_editor.survey_name")}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder={t("survey_editor.survey_name_placeholder")}
          className={`border ${isDarkMode ? "border-gray-700 bg-gray-800 text-white focus:ring-indigo-500" : "border-indigo-300 bg-white text-black focus:ring-indigo-500"}`}
        />

        <DragDropContext
          onDragEnd={(result) => {
            if (!result.destination) return;
            const reorderedQuestions = Array.from(draft.questions);
            const [removed] = reorderedQuestions.splice(result.source.index, 1);
            reorderedQuestions.splice(result.destination.index, 0, removed);
            setDraft({ ...draft, questions: reorderedQuestions });
          }}
        >
          <Droppable droppableId="questions">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-4 mt-6 max-h-[50vh] overflow-y-auto"
              >
                {draft.questions.map((q, index) => (
                  <Draggable key={q.id} draggableId={q.id.toString()} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`p-4 border rounded-lg flex justify-between items-center ${
                          q.active === false
                            ? isDarkMode ? "bg-gray-900 border-gray-700 opacity-60" : "bg-gray-50 border-gray-200 opacity-60"
                            : isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
                        }`}
                      >
                        <div>
                          <h3 className={`text-lg font-semibold ${q.active === false ? "line-through opacity-70" : ""} ${isDarkMode ? "text-indigo-300" : "text-indigo-600"}`}>{q.label}</h3>
                          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                            {t("survey_editor.type_label", {
                              type: t(`survey_editor.types.${q.type}`, q.type),
                            })}
                          </p>
                          {q.is_mandatory && (
                            <p className={`text-xs mt-1 ${q.active === false ? "text-gray-500" : "text-amber-500"}`}>
                              {t("survey_editor.mandatory")}
                              {q.active === false ? ` · ${t("survey_editor.disabled")}` : ""}
                            </p>
                          )}
                        </div>
                        <div className="space-x-2 flex items-center">
                          {q.is_mandatory ? (
                            <>
                              {q.type === "scale" && (
                                <Button
                                  onClick={() => setEditingQuestion(withDefaults(q))}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                                >
                                  {t("survey_editor.configure")}
                                </Button>
                              )}
                              <button
                                onClick={() => handleToggleMandatory(q.id)}
                                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                  q.active !== false ? "bg-green-500" : isDarkMode ? "bg-gray-600" : "bg-gray-300"
                                }`}
                                title={q.active !== false
                                  ? t("survey_editor.toggle_deactivate")
                                  : t("survey_editor.toggle_activate")}
                              >
                                <span
                                  className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    q.active !== false ? "translate-x-5" : "translate-x-0"
                                  }`}
                                />
                              </button>
                            </>
                          ) : (
                            <>
                              <Button
                                onClick={() => setEditingQuestion(withDefaults(q))}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                              >
                                {t("survey_editor.edit")}
                              </Button>
                              <Button
                                onClick={() => handleDeleteQuestion(q.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                              >
                                {t("survey_editor.delete")}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="flex justify-between mt-6">
          <Button onClick={handleAddQuestion} className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg">
            ➕ {t("survey_editor.add_question")}
          </Button>
          <div className="space-x-4">
            <Button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg">
              {t("survey_editor.cancel")}
            </Button>
            <Button onClick={handleSaveSurvey} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg">
              {t("survey_editor.save_survey")}
            </Button>
          </div>
        </div>
      </div>
      {renderQuestionEditor()}
    </div>
  );
};

export default SurveyEditor;